import './firstImpression';

const TRIGGER = {
    AFTER_LOAD_FIVE: 'afterLoadFive',
    AFTER_LOAD_TEN: 'afterLoadTen',
    ON_PAGE_SCROLL: 'onPageScroll',
    EXIT_INTENT_TRIGGER: 'exitIntentTrigger',
    AFTER_INACTIVITY_TEN: 'afterInactivityTen'
};

const FOCUSABLE_SELECTOR = 'a, button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])';

export default class Popup {
    constructor(options) {
        let componentId = options.componentId;
        let contentVersionId = options.contentVersionId;
        this._componentEl = options.componentEl;
        this._pageId = options.pageId;
        this._preset = options.preset;
        this._showAfterPeriod = options.showAfterPeriod;
        this._trigger = options.trigger;
        this._cookieName = `bento3.popup.${componentId}.${contentVersionId}`;
        const bodyId = 'page-' + this._pageId;

        this._elemCache = {
            closeBtn: this._componentEl.querySelector('.close-popup'),
            actionBtn: this._componentEl.querySelector('.popup-cta-link'),
            popup: this._componentEl.querySelector('.popup'),
            modal: this._componentEl.querySelector('.popup-modal'),
            popupOverlay: this._componentEl.querySelector('.popup-overlay'),
            body: document.getElementById(bodyId),
            stationHeader: document.querySelector('.station-header')
        };
        this.init();
    }

    init = () => {
        if (firstImpression(this._cookieName)) {
            firstImpression(this._cookieName, null);

            switch (this._trigger.value) {
                case TRIGGER.ON_PAGE_SCROLL:
                    document.addEventListener('scroll', this.displayPopup, true);
                    break;
                case TRIGGER.EXIT_INTENT_TRIGGER:
                    document.addEventListener('mouseout', (event) => {
                        //mouse out of document
                        if (event.toElement == null && event.relatedTarget == null) {
                            this.displayPopup();
                        }
                    });
                    window.addEventListener('blur', this.displayPopup); //shifting focus away from the current window
                    break;
                case TRIGGER.AFTER_INACTIVITY_TEN:
                    window.addEventListener('DOMContentLoaded', () => this.inactivityTime(600000)); //10 minutes = 1000 x 60 x 10
                    break;
                case TRIGGER.AFTER_LOAD_FIVE:
                    setTimeout(this.displayPopup, 5000);
                    break;
                case TRIGGER.AFTER_LOAD_TEN:
                    setTimeout(this.displayPopup, 10000);
                    break;
                default:
                    this.displayPopup();
            }
        }
    };

    displayPopup = () => {
        this._previouslyFocusedElement = document.activeElement;
        this._elemCache.popup.classList.add('open');
        window.addEventListener('resize', this.setBodyPadding);
        window.addEventListener('resize', this.moveStationHeader);

        const { closeBtn, popupOverlay, actionBtn } = this._elemCache;
        let allComponents = document.getElementsByClassName('component');
        for (let i = 0; i < allComponents.length; i++) {
            allComponents[i].setAttribute('aria-hidden', 'true');
        }
        this._elemCache.popup.setAttribute('aria-hidden', 'false');

        this.disableTabIndex();
        this.setTabIndex(this._elemCache.popup, '0');

        this.setBodyPadding();
        this.moveStationHeader();

        if (closeBtn) {
            closeBtn.addEventListener('click', this.closePopup);
            closeBtn.focus();
        }
        if (popupOverlay) {
            popupOverlay.addEventListener('click', this.closePopup);
            document.addEventListener('keydown', (event) => {
                if (event.key === 'Escape') {
                    this.closePopup();
                }
            });
        }

        if (actionBtn) {
            // this was added in order to prevent infinite loop of popup opening in case the action button
            // is a link that points to an internal page
            actionBtn.addEventListener('click', this.closePopup, { once: true });
        }

        //remove triggers
        switch (this._trigger.value) {
            case TRIGGER.ON_PAGE_SCROLL:
                document.removeEventListener('scroll', this.displayPopup);
                break;
            case TRIGGER.EXIT_INTENT_TRIGGER:
                document.removeEventListener('mouseout', this.displayPopup);
                window.removeEventListener('pagehide', this.displayPopup);
                break;
            case TRIGGER.AFTER_INACTIVITY_TEN:
                window.removeEventListener('DOMContentLoaded', this.inactivityTime);
                let events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
                events.forEach((eventName) => {
                    document.removeEventListener(eventName, this.displayPopup);
                });
                break;
        }
    };

    closePopup = () => {
        this._elemCache.popup.classList.add('closed');
        this._elemCache.popup.classList.remove('open');
        let allComponents = document.getElementsByClassName('component');
        for (let i = 0; i < allComponents.length; i++) {
            allComponents[i].setAttribute('aria-hidden', 'false');
        }
        this._elemCache.popup.setAttribute('aria-hidden', 'true');

        this.restoreTabIndex();
        this.setTabIndex(this._elemCache.popup, '-1');
        this.resetBodyPadding();

        // Restore focus to the element that was focused before the popup opened
        if (this._previouslyFocusedElement && document.contains(this._previouslyFocusedElement)) {
            this._previouslyFocusedElement.focus();
        }

        if (this._showAfterPeriod === 'True') {
            firstImpression(this._cookieName, 7); //set cookie with an expiration of 7 days
        } else {
            firstImpression(this._cookieName);
        }
    };

    setTabIndex = (element, tabIndex) => {
        let focusableElements = element.querySelectorAll(FOCUSABLE_SELECTOR);
        for (let i = 0; i < focusableElements.length; i++) {
            focusableElements[i].setAttribute('tabindex', tabIndex);
        }
    };

    /**
     * Disable tab navigation for all focusable elements outside the popup.
     * Saves each element's original tabindex so it can be restored later,
     * preventing dynamically appended content (e.g. JAWS TV schedules)
     * from being permanently stuck at tabindex="-1" after the popup closes.
     *
     * This method is idempotent: if _savedTabIndexes already exists from a
     * previous call, it is a no-op so repeated displayPopup invocations
     * don't overwrite the original values with already-disabled state.
     */
    disableTabIndex = () => {
        if (this._savedTabIndexes) {
            return;
        }

        const popupEl = this._elemCache.popup;
        const focusableElements = document.querySelectorAll(FOCUSABLE_SELECTOR);
        this._savedTabIndexes = [];

        for (let i = 0; i < focusableElements.length; i++) {
            const el = focusableElements[i];
            // Skip elements inside the popup — they stay focusable
            if (popupEl && popupEl.contains(el)) {
                continue;
            }
            this._savedTabIndexes.push({
                element: el,
                original: el.hasAttribute('tabindex') ? el.getAttribute('tabindex') : null
            });
            el.setAttribute('tabindex', '-1');
        }
    };

    /**
     * Restore the original tabindex values saved by disableTabIndex().
     * Elements that did not have a tabindex attribute before get it removed
     * rather than set to "0", so they return to their native tab order.
     */
    restoreTabIndex = () => {
        if (!this._savedTabIndexes) {
            return;
        }
        for (let i = 0; i < this._savedTabIndexes.length; i++) {
            const { element, original } = this._savedTabIndexes[i];
            if (!document.contains(element)) {
                continue;
            }
            if (original === null) {
                element.removeAttribute('tabindex');
            } else {
                element.setAttribute('tabindex', original);
            }
        }
        this._savedTabIndexes = null;
    };

    setBodyPadding = () => {
        if (!this._elemCache.popup.classList.contains('closed')) {
            if (this._preset === 'fixedTop') {
                let modalHeight = this._elemCache.modal.offsetHeight + 'px';
                this._elemCache.body.style['padding-top'] = modalHeight;
                if (
                    this._elemCache.stationHeader &&
                    this._elemCache.stationHeader.classList.contains('navbar-fixed-top')
                ) {
                    this._elemCache.stationHeader.style['top'] = modalHeight;
                }
            }
            if (this._preset === 'fixedBottom') {
                let modalHeight = this._elemCache.modal.offsetHeight + 'px';
                this._elemCache.body.style['padding-bottom'] = modalHeight;
            }
        }
    };

    resetBodyPadding = () => {
        if (this._preset === 'fixedTop') {
            this._elemCache.body.style['padding-top'] = 0;
            if (
                this._elemCache.stationHeader &&
                this._elemCache.stationHeader.classList.contains('navbar-fixed-top')
            ) {
                this._elemCache.stationHeader.style['top'] = 0;
            }
        }
        if (this._preset === 'fixedBottom') {
            this._elemCache.body.style['padding-bottom'] = 0;
        }
    };

    moveStationHeader = () => {
        const observer = new MutationObserver(() => {
            if (this._elemCache.body.classList.contains('nav-is-open')) {
                this.resetBodyPadding();
            } else {
                setTimeout(() => {
                    this.setBodyPadding();
                }, 500);
            }
        });

        observer.observe(this._elemCache.body, {
            attributes: true,
            attributeFilter: ['class'],
            childList: false,
            characterData: false
        });
    };

    inactivityTime = (t) => {
        let time;
        const that = this;
        let events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];
        events.forEach((eventName) => document.addEventListener(eventName, resetTimer, true));

        function resetTimer() {
            clearTimeout(time);
            time = setTimeout(that.displayPopup, t);
        }
    };
}
